import _ from "lodash";
import getAllTiposIndicador from "entity/TipoIndicador/all";
import { getIndicadorByField } from "entity/Indicador/all";
import Cliente from "entity/Cliente";

/**
 * Adiciona (backfill) **apenas** os campos `acima` e `abaixo` aos apontamentos de um laudo antigo,
 * usando os parâmetros do cliente **salvos dentro do próprio laudo** (snapshot de quando o laudo foi
 * salvo pela última vez) — nunca os parâmetros atuais/ao vivo do cliente.
 *
 * Diferente de `recalcularConformidadeLaudo`, esta função é cirúrgica: ela NÃO regrava `conformidade`,
 * `conforme`, `conformeCol*` nem qualquer outro campo. `acima`/`abaixo` só nascem como efeito colateral
 * de `calcConformidade`, então rodamos esse cálculo num clone descartável de cada apontamento (com os
 * params históricos) apenas para gerar esses dois campos e copiamos somente eles de volta. Como os
 * params usados são os mesmos que geraram a `conformidade` original, o restante ficaria idêntico — logo
 * não há motivo para reescrevê-lo, e assim eliminamos qualquer risco de alterar valores já corretos.
 *
 * Não grava nada no banco — apenas retorna a árvore de apontamentos com `acima`/`abaixo` preenchidos e
 * um relatório de diffs para revisão antes de salvar. Indicadores sem `calcConformidade` implementado
 * (método abstrato da classe base) são detectados e deixados intocados.
 *
 * @param {*} laudoData `data` de uma instância de LaudoVisita já carregada (após onLoad).
 * @param {*} clienteSnapshot Snapshot de `laudoData.cliente` capturado ANTES de `onLoad()`
 * sobrescrevê-lo com os dados atuais do cliente (ver `LaudoVisita.loadFazenda`).
 */
export default function adicionarAcimaAbaixoLaudo(laudoData, clienteSnapshot) {
    const apontamentosOriginais = laudoData.apontamentos || {};
    const apontamentosResultado = _.cloneDeep(apontamentosOriginais);

    const indicadores = [];
    const indicadoresIgnorados = [];
    let totalAlteracoes = 0;

    if (!clienteSnapshot?.params) {
        return {
            apontamentos: apontamentosOriginais,
            indicadores: [],
            indicadoresIgnorados: [],
            totalAlteracoes: 0,
            semParametrosHistoricos: true,
        };
    }

    const mudou = (antes, depois) => depois !== undefined && String(antes ?? "") !== String(depois ?? "");

    for (const TipoClass of getAllTiposIndicador()) {
        const porIndicador = apontamentosResultado[TipoClass.field];
        if (!porIndicador) continue;

        for (const indicadorField of Object.keys(porIndicador)) {
            const IndicadorClass = getIndicadorByField(indicadorField);
            const apontamentosArr = porIndicador[indicadorField];

            if (!IndicadorClass || !Array.isArray(apontamentosArr)) continue;

            const diffsApontamentos = [];
            let erroCalculo = null;

            apontamentosArr.forEach((apontamento, index) => {
                if (erroCalculo) return;

                try {
                    const tipoName = apontamento.tipo;
                    const params = tipoName
                        ? Cliente.getParamsClienteData(clienteSnapshot, IndicadorClass, tipoName, TipoClass)
                        : null;

                    // Roda o cálculo num clone descartável só para gerar acima/abaixo — o apontamento
                    // real (e sua conformidade) permanece intocado.
                    const calcClone = _.cloneDeep(apontamento);
                    IndicadorClass.calcConformidade(calcClone, params);

                    // Copia SOMENTE acima/abaixo de nível apontamento.
                    const mudouApontamento =
                        mudou(apontamento.acima, calcClone.acima) || mudou(apontamento.abaixo, calcClone.abaixo);
                    if (calcClone.acima !== undefined) apontamento.acima = calcClone.acima;
                    if (calcClone.abaixo !== undefined) apontamento.abaixo = calcClone.abaixo;

                    // Copia SOMENTE acima/abaixo de nível coleta.
                    let coletasAlteradas = 0;
                    (apontamento.coletas || []).forEach((coleta, coletaIndex) => {
                        const coletaCalc = calcClone.coletas?.[coletaIndex];
                        if (!coletaCalc) return;

                        const mudouColeta =
                            mudou(coleta.acima, coletaCalc.acima) || mudou(coleta.abaixo, coletaCalc.abaixo);
                        if (coletaCalc.acima !== undefined) coleta.acima = coletaCalc.acima;
                        if (coletaCalc.abaixo !== undefined) coleta.abaixo = coletaCalc.abaixo;
                        if (mudouColeta) coletasAlteradas++;
                    });

                    if (mudouApontamento || coletasAlteradas) {
                        diffsApontamentos.push({
                            index,
                            talhao: apontamento.talhao?.nome || null,
                            apontamentoAlterado: mudouApontamento,
                            acima: apontamento.acima ?? null,
                            abaixo: apontamento.abaixo ?? null,
                            coletasAlteradas,
                        });
                    }
                } catch (error) {
                    erroCalculo = error.message || "Cálculo de conformidade não implementado para este indicador";
                }
            });

            if (erroCalculo) {
                // Reverte qualquer alteração parcial feita antes do erro ser detectado.
                porIndicador[indicadorField] = _.cloneDeep(
                    _.get(apontamentosOriginais, `${TipoClass.field}.${indicadorField}`) || [],
                );
                indicadoresIgnorados.push({
                    tipoField: TipoClass.field,
                    tipoFullName: TipoClass.fullName,
                    indicadorField,
                    indicadorFullName: IndicadorClass.fullName,
                    motivo: erroCalculo,
                });
            } else if (diffsApontamentos.length) {
                indicadores.push({
                    tipoField: TipoClass.field,
                    tipoFullName: TipoClass.fullName,
                    indicadorField,
                    indicadorFullName: IndicadorClass.fullName,
                    apontamentos: diffsApontamentos,
                });

                totalAlteracoes += diffsApontamentos.reduce(
                    (soma, ap) => soma + (ap.apontamentoAlterado ? 1 : 0) + ap.coletasAlteradas,
                    0,
                );
            }
        }
    }

    return {
        apontamentos: apontamentosResultado,
        indicadores,
        indicadoresIgnorados,
        totalAlteracoes,
        semParametrosHistoricos: false,
    };
}
